import {
  type Msg,
  MsgRedactedBatchCalls,
  MsgRedactedCall,
  MsgRedactedDepositToSubWallet,
  MsgRedactedGasTankWithdraw,
  MsgRedactedThorchainDeposit,
  MsgRedactedWithdraw,
  MsgRedactedWithdrawFromSubWallet,
} from "rujira.js";
import { canonicalCalls, type V2Call, type V2Coin } from "./action";
import { normalizePrivateV2Call } from "./calls";
import type { V2ContractConfig, V2RelayerMetadata } from "./client";
import { base64Encode, utf8 } from "./encoding";
import { validateCallFeeQuote, type V2CallFeeQuote } from "./feeRules";
import {
  assertRelayerFeeWithinLimit,
  privateCallTotals,
  protocolFeeCoins,
  selectPrivateCallRelayerFee,
} from "./safety";

export type V2PrivateFeeQuote = Readonly<{
  id: string;
  expiresAt: number;
  source: "reserve" | "spending";
  relayerFee?: Readonly<V2Coin>;
  protocolFees: readonly Readonly<V2Coin>[];
  protocolMode: "deducted" | "additional";
  amounts: readonly Readonly<V2Coin>[];
  /** Declared debits, not net batch cash flow or downstream THORChain fees. */
  totalDebit: readonly Readonly<V2Coin>[];
  received?: readonly Readonly<V2Coin>[];
  /** Model B: Reserve transfers into the one-time sub before the call. */
  funding?: readonly Readonly<V2FundingLeg>[];
}>;

export type V2FundingLeg = {
  denom: string;
  needed: string;
  present: string;
  transfer: string;
  fee: string;
  relayerFee?: string;
};

export class V2FeeReviewError extends Error {
  readonly code = "PRIVATE_FEE_REVIEW_REQUIRED";
  constructor() {
    super(
      "Private transaction details or fees changed. Review the updated fees and confirm again."
    );
    this.name = "V2FeeReviewError";
  }
}
export const isV2FeeReviewError = (error: unknown): boolean =>
  error instanceof V2FeeReviewError ||
  (typeof error === "object" &&
    error !== null &&
    "code" in error &&
    error.code === "PRIVATE_FEE_REVIEW_REQUIRED");

export type V2PrivateIntent =
  | { kind: "withdraw"; denom: string; amount: string; recipient: string }
  | { kind: "transfer"; denom: string; amount: string }
  | { kind: "shield"; denom: string; amount: string }
  | { kind: "call"; calls: V2Call[] };

const deepFreeze = <T>(value: T): T => {
  if (value && typeof value === "object") {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
};

/** Snapshot every executable field; caller mutations cannot change an approved action. */
export const privateIntent = (message: Msg): V2PrivateIntent => {
  let value: V2PrivateIntent;
  if (
    message instanceof MsgRedactedWithdraw ||
    message instanceof MsgRedactedGasTankWithdraw
  ) {
    value = {
      kind: "withdraw",
      denom: message.denom,
      amount: message.amount,
      recipient: message.account.address,
    };
  } else if (message instanceof MsgRedactedDepositToSubWallet) {
    value = { kind: "transfer", denom: message.denom, amount: message.amount };
  } else if (message instanceof MsgRedactedWithdrawFromSubWallet) {
    value = { kind: "shield", denom: message.denom, amount: message.amount };
  } else if (message instanceof MsgRedactedThorchainDeposit) {
    const match = message.asset.asset.match(/^([A-Z0-9]+)[.-](.+)$/);
    if (!match) throw new Error("Invalid THORChain asset");
    const [, chain, symbol] = match;
    value = {
      kind: "call",
      calls: [
        {
          thorchain_deposit: {
            denom: message.denom,
            amount: message.amount,
            memo: message.memo,
            asset: {
              chain,
              symbol,
              ticker: symbol.split("-")[0],
              secured: message.asset.type === "SECURED",
            },
          },
        },
      ],
    };
  } else if (message instanceof MsgRedactedCall) {
    value = {
      kind: "call",
      calls: [
        {
          wasm_execute: {
            contract_addr: message.contractAddress,
            msg: base64Encode(utf8(message.call)),
            funds:
              BigInt(message.amount) > 0n
                ? [{ denom: message.denom, amount: message.amount }]
                : [],
          },
        },
      ],
    };
  } else if (message instanceof MsgRedactedBatchCalls) {
    value = {
      kind: "call",
      calls: message.calls.filter(Boolean).map(normalizePrivateV2Call),
    };
  } else {
    throw new Error("This transaction is not supported in Private Mode");
  }
  // Copy nested arrays from batch messages before freezing them.
  const snapshot = JSON.parse(JSON.stringify(value)) as V2PrivateIntent;
  if (snapshot.kind === "call") canonicalCalls(snapshot.calls);
  else if (
    !snapshot.denom ||
    !/^[1-9]\d*$/.test(snapshot.amount) ||
    BigInt(snapshot.amount) > (1n << 128n) - 1n
  ) {
    throw new Error("Invalid private transfer amount");
  }
  return deepFreeze(snapshot);
};

export type V2FeeTerms = Omit<V2PrivateFeeQuote, "id" | "expiresAt">;

const coins = (totals: ReadonlyMap<string, bigint>): V2Coin[] =>
  [...totals]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .filter(([, amount]) => amount > 0n)
    .map(([denom, amount]) => ({ denom, amount: amount.toString() }));

/** Read-only quote. Full relayer simulation remains authoritative for atomic batches. */
export const quotePrivateFeeTerms = async (params: {
  intent: V2PrivateIntent;
  config: V2ContractConfig;
  metadata: V2RelayerMetadata;
  spendingBalance: (denom: string) => Promise<bigint>;
  /** Query the contract for this session's active Spending account. */
  callFeeQuote?: (calls: readonly V2Call[]) => Promise<V2CallFeeQuote>;
}): Promise<V2FeeTerms> => {
  const { intent, config, metadata } = params;
  const totals =
    intent.kind === "call"
      ? privateCallTotals(intent.calls)
      : new Map([[intent.denom, BigInt(intent.amount)]]);
  const amounts = coins(totals);
  const source =
    intent.kind === "call" || intent.kind === "shield" ? "spending" : "reserve";
  const protocolMode = source === "reserve" ? "deducted" : "additional";
  const strict = config.fee_rules != null;
  if (
    strict &&
    (!Number.isSafeInteger(config.fee_policy_version) ||
      !(Number(config.fee_policy_version) > 0) ||
      !Number.isSafeInteger(config.protocol_fee_bps) ||
      config.protocol_fee_bps < 0 ||
      config.protocol_fee_bps > 1_000)
  )
    throw new Error("Invalid product fee policy version");
  if (intent.kind === "call" && intent.calls.length > metadata.maxCalls)
    throw new Error("Private call exceeds relayer policy");
  let callQuote: V2CallFeeQuote | undefined;
  if (strict && intent.kind === "call") {
    if (!params.callFeeQuote)
      throw new Error("An on-chain private fee quote is required");
    callQuote = validateCallFeeQuote(
      await params.callFeeQuote(intent.calls),
      config
    );
  }
  // Legacy deployments floor each outer call separately. Strict product calls
  // use the contract's classified, aggregate-ceiling quote instead.
  const protocolTotals = new Map<string, bigint>();
  const feeInputs = callQuote
    ? []
    : intent.kind === "call"
      ? intent.calls.map((call) => privateCallTotals([call]))
      : intent.kind === "shield" || (strict && intent.kind === "transfer")
        ? []
        : [totals];
  for (const input of feeInputs) {
    for (const fee of protocolFeeCoins(
      input,
      config.protocol_fee_bps,
      strict ? "ceil" : "floor"
    )) {
      protocolTotals.set(
        fee.denom,
        (protocolTotals.get(fee.denom) ?? 0n) + BigInt(fee.amount)
      );
    }
  }
  const protocolFees = callQuote?.protocol_fees ?? coins(protocolTotals);
  if (
    strict &&
    intent.kind === "withdraw" &&
    protocolFees.some((fee) => BigInt(fee.amount) >= BigInt(intent.amount))
  )
    throw new Error(
      "The withdrawal amount must be larger than its privacy fee"
    );
  let relayerFee: V2Coin | undefined;
  if (intent.kind === "call") {
    if (
      callQuote?.relayer_fee_allowed === false &&
      !metadata.sponsoredActions.has("call_subwallet")
    )
      throw new Error(
        "This action requires a sponsored relayer. No public-wallet transaction was submitted."
      );
    relayerFee = await selectPrivateCallRelayerFee({
      calls: intent.calls,
      minimumFees: metadata.minimumFees,
      sponsored: metadata.sponsoredActions.has("call_subwallet"),
      configuredCaps: config.call_relayer_fee_caps ?? [],
      balance: params.spendingBalance,
    });
  } else if (intent.kind === "shield") {
    if (!metadata.sponsoredActions.has("shield_from_subwallet")) {
      throw new Error(
        "The relayer does not sponsor transfers to Private Reserve. No public-wallet transaction was submitted."
      );
    }
  } else {
    const action =
      intent.kind === "withdraw" ? "withdraw" : "transfer_to_subwallet";
    if (!metadata.sponsoredActions.has(action)) {
      if (strict && intent.kind === "transfer")
        throw new Error(
          "Transfers to Spending require a sponsored relayer. No public-wallet transaction was submitted."
        );
      const amount = metadata.minimumFees.get(intent.denom);
      if (!amount || amount <= 0n)
        throw new Error("No compatible relayer fee for this reserve transfer");
      assertRelayerFeeWithinLimit(BigInt(intent.amount), amount);
      relayerFee = { denom: intent.denom, amount: amount.toString() };
    }
  }
  const debit = new Map(totals);
  const add = (coin: Readonly<V2Coin>) =>
    debit.set(coin.denom, (debit.get(coin.denom) ?? 0n) + BigInt(coin.amount));
  if (relayerFee) add(relayerFee);
  if (protocolMode === "additional") protocolFees.forEach(add);
  const received = new Map(totals);
  if (protocolMode === "deducted")
    protocolFees.forEach((fee) =>
      received.set(
        fee.denom,
        (received.get(fee.denom) ?? 0n) - BigInt(fee.amount)
      )
    );
  return deepFreeze({
    source,
    relayerFee,
    protocolFees,
    protocolMode,
    amounts,
    totalDebit: coins(debit),
    received: intent.kind === "call" ? undefined : coins(received),
  });
};

export const privateFeePolicy = (
  config: V2ContractConfig,
  metadata: V2RelayerMetadata
): string =>
  JSON.stringify({
    protocol: config.protocol_version,
    code: config.subwallet_code_id,
    checksum: config.subwallet_code_checksum,
    collector: config.fee_collector,
    bps: config.protocol_fee_bps,
    feePolicyVersion: config.fee_policy_version ?? null,
    feeRules: config.fee_rules ?? null,
    caps: config.call_relayer_fee_caps ?? [],
    // Any of the relayer's signers may send it: bind the relayer, not the signer.
    relayer: metadata.primaryAddress ?? metadata.address,
    minimum: [...metadata.minimumFees]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([denom, amount]) => [denom, amount.toString()]),
    sponsored: [...metadata.sponsoredActions].sort(),
    maxCalls: metadata.maxCalls,
    maxMessageBytes: metadata.maxMessageBytes,
  });

/** In-memory, one-use capability: IDs or copied objects do not authorize fees. */
export class V2FeeQuoteBook {
  private readonly issued = new WeakMap<
    V2PrivateFeeQuote,
    {
      binding: string;
      intent: V2PrivateIntent;
      policy: string;
      terms: V2FeeTerms;
    }
  >();
  issue(
    binding: string,
    intent: V2PrivateIntent,
    policy: string,
    terms: V2FeeTerms,
    now = Date.now()
  ): V2PrivateFeeQuote {
    const quote = deepFreeze({
      ...terms,
      id: crypto.randomUUID(),
      expiresAt: now + 120_000,
    });
    this.issued.set(quote, { binding, intent, policy, terms });
    return quote;
  }
  consume(
    quote: V2PrivateFeeQuote | undefined,
    binding: string,
    intent: V2PrivateIntent,
    now = Date.now()
  ) {
    const record = quote && this.issued.get(quote);
    if (
      !record ||
      !quote ||
      now >= quote.expiresAt ||
      record.binding !== binding ||
      JSON.stringify(record.intent) !== JSON.stringify(intent)
    )
      throw new V2FeeReviewError();
    this.issued.delete(quote);
    return record;
  }
}

export const assertPrivateFeeTermsUnchanged = (
  review: { policy: string; terms: V2FeeTerms },
  policy: string,
  terms: V2FeeTerms
): void => {
  if (
    review.policy !== policy ||
    JSON.stringify(review.terms) !== JSON.stringify(terms)
  )
    throw new V2FeeReviewError();
};
